import { useMemo } from 'react';
import type { RegionVisibility, RegionEngineScore } from '../types/index.js';
import type { EngineAggregation } from './EngineVisibilityCard.js';
import { EngineVisibilityCard } from './EngineVisibilityCard.js';
import { RegionFidelityLabel, RegionPartialCoverage, effectiveRegion } from './RegionLabels.js';
import { ENGINE_LABELS } from '../types/index.js';
import { Icon } from './ui/Icon.js';

/**
 * Spec 171 — per-region visibility split.
 *
 * When a brand is monitored in ≥2 regions, the default brand view splits the
 * visibility cards by region, shown SIDE BY SIDE (US vs DK columns) — never a
 * blended cross-region number (the 170 never-blend invariant carried to render).
 * Region is the primary split. Each region column carries its fidelity label, and
 * each engine that can't be pinned to the region is marked "Partial coverage".
 *
 * This component is ONLY mounted when `regions.length >= 2`. Single-region brands
 * keep the existing flat "Visibility by engine" section, byte-identical to today.
 */

const ENGINE_ORDER = ['chatgpt', 'gemini', 'claude', 'google_aio', 'mistral'];

/** Map a lean per-(region,engine) score onto the EngineVisibilityCard shape. */
function toAggregation(score: RegionEngineScore): EngineAggregation {
  return {
    engine: score.engine,
    mention_rate: score.mention_rate,
    // The region read groups by region first and doesn't carry position /
    // sentiment / unprompted, so the card renders those as "--" and falls back
    // to the raw mention rate for its headline.
    avg_position: null,
    avg_sentiment: null,
    share_of_voice: score.share_of_voice,
    sample_size: score.sample_size,
    reliability: score.reliability_tier,
    unprompted_sample_size: 0,
    unprompted_mention_rate: null,
    // Citation visibility DOES come through the region read, per (region,
    // engine), so it must be mapped rather than hard-coded like the fields
    // above. Defaulting these to 0/null would make every engine in the
    // multi-region layout claim "Not evaluable" — a fabricated absence, which
    // is precisely the class of dishonesty this measurement exists to remove.
    grounded_sample_size: score.grounded_sample_size,
    brand_retrieved_count: score.brand_retrieved_count,
    brand_cited_count: score.brand_cited_count,
    brand_retrieval_rate: score.brand_retrieval_rate,
    brand_citation_rate: score.brand_citation_rate,
    // The unprompted-population counters ride through too. In this layout the
    // card falls back to the raw mention rate (unprompted_mention_rate is null
    // above), so it will read the all-prompt pair — but the two populations must
    // stay wired to the same source, or a later change that gives the region
    // read an unprompted headline would silently start comparing a region's
    // unprompted mention rate against an all-prompt citation rate.
    unprompted_grounded_sample_size: score.unprompted_grounded_sample_size,
    unprompted_brand_cited_count: score.unprompted_brand_cited_count,
    unprompted_brand_citation_rate: score.unprompted_brand_citation_rate,
  };
}

function sortEngines(engines: RegionEngineScore[]): RegionEngineScore[] {
  return [...engines].sort((a, b) => {
    const ai = ENGINE_ORDER.indexOf(a.engine);
    const bi = ENGINE_ORDER.indexOf(b.engine);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function RegionColumn({ region }: { region: RegionVisibility }) {
  const engines = sortEngines(region.engines);

  return (
    <div className="space-y-3" data-testid="region-visibility-column">
      {/* Region column header — name, home marker, region-level fidelity */}
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 pb-2">
        <span className="text-sm font-semibold text-slate-800">
          {region.display_name}
        </span>
        <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-500">
          {region.region_code}
        </span>
        {region.is_home && (
          <span
            className="inline-flex items-center gap-1 rounded-md bg-violet-100 px-1.5 py-0.5 text-xs font-medium text-violet-700"
            title="The brand's home region."
          >
            <Icon name="bx-home" /> Home
          </span>
        )}
        <RegionFidelityLabel
          fidelity={region.region_fidelity}
          mechanism={region.region_mechanism}
        />
      </div>

      {/* Per-engine cards for this region */}
      <div className="space-y-4">
        {engines.length === 0 ? (
          <p className="hub-plate hub-plate--flat px-3 py-6 text-center text-xs text-[var(--hub-color-ink-muted)]">
            No measured engines for this region yet.
          </p>
        ) : (
          engines.map((score) => {
            const { fidelity, mechanism } = effectiveRegion(
              region.region_fidelity,
              region.region_mechanism,
              score.region_fidelity,
              score.region_mechanism,
            );
            return (
              <div key={score.engine} className="space-y-1.5">
                <EngineVisibilityCard data={toAggregation(score)} />
                <div className="flex flex-wrap items-center gap-1.5 px-1">
                  <RegionFidelityLabel
                    fidelity={fidelity}
                    mechanism={mechanism}
                    showMechanism={false}
                  />
                  <RegionPartialCoverage fidelity={fidelity} />
                  <span className="sr-only">
                    {ENGINE_LABELS[score.engine] ?? score.engine} in {region.display_name}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export function RegionVisibilitySplit({
  regions,
  freshness,
}: {
  regions: RegionVisibility[];
  /**
   * Visibility roll-up freshness from pipeline/status. When the materialized
   * scores are stale, freshly-captured engines (e.g. a just-funded Claude) are
   * captured + analyzed but not yet SCORED into this split — so we say so rather
   * than let the reader misread the omission as "that engine has no data".
   */
  freshness?: { lastComputedAt: string | null; isStale: boolean };
}) {
  // Home region first, then the rest in stable order — so the column the client
  // recognizes (their own market) leads the side-by-side comparison.
  const ordered = useMemo(() => {
    return [...regions].sort((a, b) => {
      if (a.is_home && !b.is_home) return -1;
      if (!a.is_home && b.is_home) return 1;
      return a.display_name.localeCompare(b.display_name);
    });
  }, [regions]);

  // Side-by-side columns. Two regions → two columns on large screens; more
  // regions wrap into a responsive grid. Never a single blended figure.
  const colClass =
    ordered.length === 2
      ? 'lg:grid-cols-2'
      : ordered.length >= 3
        ? 'lg:grid-cols-2 xl:grid-cols-3'
        : 'lg:grid-cols-1';

  return (
    <section className="mt-8" data-testid="region-visibility-split">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-semibold text-slate-900">Visibility by region</h2>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">
          {ordered.length} regions
        </span>
      </div>
      <p className="mt-1 text-xs text-slate-400">
        Different regions get different AI answers — shown side by side, never blended.
        Each engine is labeled by how faithfully its region was applied.
      </p>
      {freshness?.lastComputedAt && (
        <p
          data-testid="region-visibility-freshness"
          className={`mt-1.5 text-xs ${freshness.isStale ? 'text-amber-600' : 'text-slate-400'}`}
        >
          {freshness.isStale ? (
            <>
              Scores last updated {timeAgo(freshness.lastComputedAt)} — newer
              captures may not be reflected here yet (recompute pending).
            </>
          ) : (
            <>Scores updated {timeAgo(freshness.lastComputedAt)}.</>
          )}
        </p>
      )}
      <div className={`mt-4 grid grid-cols-1 gap-6 ${colClass}`}>
        {ordered.map((region) => (
          <RegionColumn key={region.region_code} region={region} />
        ))}
      </div>
    </section>
  );
}
